import { useEffect, useRef, useState } from 'react';

import type { Strategy } from '@/features/auth/login/types/login';
import { initiateOAuthRedirect } from '@/features/auth/login/utils/oauthRedirect';
import { SSO_ERROR_KEY } from '@/features/auth/shared/consts/clerk';
import type { AuthError } from '@/features/auth/shared/types/auth';
import { mapClerkError } from '@/features/auth/shared/utils/clerkErrorMapper';
import { useClerk, useSignIn } from '@clerk/react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const OAUTH_HANG_TIMEOUT_MS = 8_000;

interface UseOAuthLoginReturn {
  handleSocialLogin: (strategy: Strategy) => Promise<void>;
  oauthLoading: boolean;
}

export const useOAuthLogin = (setError: (error: AuthError | null) => void): UseOAuthLoginReturn => {
  const { signIn } = useSignIn();
  const clerk = useClerk();
  const { t } = useTranslation();
  const [oauthLoading, setOauthLoading] = useState(false);
  const hasResetRef = useRef(false);

  useEffect(() => {
    // Runs once when Clerk first loads (hasResetRef guards against re-runs if
    // signIn or clerk re-instantiate). Shows any stored SSO error from the
    // previous attempt and clears stale pending sign-in state so the first
    // sso() call starts clean. Doing this at mount time (not inside
    // handleSocialLogin) avoids the stale-closure problem: awaiting reset()
    // triggers a Clerk state update that replaces the signIn reference, causing
    // a subsequent sso() call in the same closure to use the old object and fail.
    if (!clerk.loaded || hasResetRef.current) return;
    hasResetRef.current = true;

    const stored = sessionStorage.getItem(SSO_ERROR_KEY);
    if (stored) {
      sessionStorage.removeItem(SSO_ERROR_KEY);
      toast.error(stored);
    }

    if (signIn.id) {
      void signIn.reset().catch((_err: unknown) => {
        // No pending sign-in or reset not supported — proceed.
      });
    }
  }, [clerk.loaded]); // intentional: clerk.loaded is the one-time trigger

  useEffect(() => {
    // bfcache: when the browser restores this page after the user presses Back
    // from the OAuth provider, Clerk's in-memory state is stale (pending nonce /
    // isTransferable). Reload to ensure sso() generates a fresh OAuth state
    // parameter instead of reusing the old one (which causes authorization_invalid).
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const handleSocialLogin = async (strategy: Strategy) => {
    setError(null);
    setOauthLoading(true);

    // Safety net: if sso() hangs (e.g. Clerk in status="error" mid-click),
    // reload after OAUTH_HANG_TIMEOUT_MS to get clean state instead of leaving
    // the button disabled forever.
    const timeoutHandle = setTimeout(() => {
      window.location.reload();
    }, OAUTH_HANG_TIMEOUT_MS);

    try {
      await initiateOAuthRedirect(signIn, strategy);
      // sso() sets window.location.href synchronously and returns — the browser
      // queues the navigation but JS keeps running until the page unloads, so this
      // line and the finally block below both execute before unload. setOauthLoading
      // is the actual fix for the rare case where sso() resolves but the redirect
      // silently does not happen (button stays enabled so the user can retry).
      setOauthLoading(false);
    } catch (err) {
      const mappedError = mapClerkError(err, t);
      setError(mappedError);
      toast.error(mappedError.message);
      setOauthLoading(false);
    } finally {
      // Runs before page unload on success (see above) and on error/hang —
      // so the watchdog never fires spuriously.
      clearTimeout(timeoutHandle);
    }
  };

  return { handleSocialLogin, oauthLoading };
};
